import { Role } from '@okletsplay/core';
import { sessionSelector } from 'app/store/session/selectors';
import { updateTournament as updateTournamentByAdmin } from 'modules/admin/api';
import { currentTournamentSelector } from 'modules/tournaments/store/current-tournament/selectors';
import { toastError, toastSuccess } from 'modules/utils';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { getTournamentById } from 'modules/tournaments/store/current-tournament/repository';
import { useStyles } from './ToggleTournamentVisibilityButton.styles';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

export const ToggleTournamentVisibilityButton: FC = () => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const user = useSelector(sessionSelector.activeUser);
  const tournament = useSelector(currentTournamentSelector.tournament);

  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));

  const [isUpdating, setIsUpdating] = useState(false);

  const updateTournament = useCallback(
    ({ isHidden }: { isHidden: boolean }) => {
      if (isUpdating) return;
      if (!tournament?.id) {
        toastError('Cannot update tournament');
        return;
      }

      const successMessage = isHidden ? `Tournament is hidden` : `Tournament is not hidden anymore`;

      updateTournamentByAdmin(tournament.id, { isHidden }).then(
        () => {
          toastSuccess(successMessage);
          setIsUpdating(false);
          dispatch(getTournamentById(tournament.id));
        },
        () => {
          setIsUpdating(false);
        }
      );
    },
    [dispatch, isUpdating, tournament.id]
  );

  const onClickHandler = useCallback(() => {
    updateTournament({ isHidden: !tournament.isHidden });
  }, [tournament.isHidden, updateTournament]);

  const buttonText = useMemo(() => {
    return tournament.isHidden ? 'Hidden' : 'Visible';
  }, [tournament.isHidden]);

  const buttonIcon = useMemo(() => {
    const visibleIcon = <VisibilityIcon fontSize="medium" />;
    const hiddenIcon = <VisibilityOffIcon fontSize="medium" />;

    return tournament.isHidden ? hiddenIcon : visibleIcon;
  }, [tournament.isHidden]);

  if (!user || !user.roles?.includes(Role.TournamentAdministrator)) {
    return null;
  }

  if (!tournament) {
    return null;
  }

  if (isLargeScreen) {
    return (
      <Button
        className={classes.button}
        disabled={isUpdating}
        startIcon={buttonIcon}
        size="medium"
        onClick={onClickHandler}
      >
        {buttonText}
      </Button>
    );
  }

  return (
    <IconButton aria-label="share" onClick={onClickHandler} disabled={isUpdating}>
      {buttonIcon}
    </IconButton>
  );
};
